import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { FileCell } from "./useViewGridColumns";

describe("FileCell context", () => {
  it("offers replace and confirmed remove only for a writable stored file", async () => {
    const authFetch = jest.fn().mockResolvedValue(undefined);
    const refresh = jest.fn();
    render(
      <FileCell
        value={{ filename: "note.txt", size_bytes: 7 }}
        rowId="row-1"
        columnField="main.attachment"
        focusTableId="table-1"
        viewId="view-1"
        writeContext={{ canvasId: "canvas-1", elementId: "file-1" }}
        allowEdit
        authFetch={authFetch}
        workspaceId="workspace-1"
        onRefresh={refresh}
      />,
    );
    expect(screen.getByTitle("Replace file")).toBeEnabled();
    fireEvent.click(screen.getByTitle("Remove file"));
    expect(authFetch).not.toHaveBeenCalled();
    fireEvent.click(screen.getByRole("button", { name: "Cancel" }));
    expect(authFetch).not.toHaveBeenCalled();
    fireEvent.click(screen.getByTitle("Remove file"));
    fireEvent.click(screen.getByRole("button", { name: "Remove" }));
    await waitFor(() =>
      expect(authFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/files/table-1/row-1/attachment?view_id=view-1&canvas_id=canvas-1&element_id=file-1",
        "remove file",
        { method: "DELETE", headers: { "x-workspace-id": "workspace-1" } },
      ),
    );
    await waitFor(() => expect(refresh).toHaveBeenCalledTimes(1));
  });

  it("replaces a stored file through upload and refreshes the server row", async () => {
    const authFetch = jest
      .fn()
      .mockResolvedValue({ filename: "replacement.txt" });
    const refresh = jest.fn();
    render(
      <FileCell
        value={{ filename: "note.txt", size_bytes: 7 }}
        rowId="row-1"
        columnField="attachment"
        focusTableId="table-1"
        viewId="view-1"
        allowEdit
        authFetch={authFetch}
        workspaceId="workspace-1"
        onRefresh={refresh}
      />,
    );
    fireEvent.change(screen.getByLabelText("Choose file to upload"), {
      target: { files: [new File(["replacement"], "replacement.txt")] },
    });
    await waitFor(() =>
      expect(authFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/files/table-1/row-1/attachment?view_id=view-1",
        "upload file",
        expect.objectContaining({ method: "POST", body: expect.any(FormData) }),
      ),
    );
    await waitFor(() => expect(refresh).toHaveBeenCalledTimes(1));
  });
  it("sends view and canvas element context on upload", async () => {
    const authFetch = jest.fn().mockResolvedValue({});
    render(
      <FileCell
        value={null}
        rowId="row-1"
        columnField="main.attachment"
        focusTableId="table-1"
        viewId="view-1"
        writeContext={{ canvasId: "canvas-1", elementId: "grid-1" }}
        allowEdit
        authFetch={authFetch}
        workspaceId="workspace-1"
      />,
    );
    expect(screen.getByTitle("Upload file")).toBeEnabled();
    fireEvent.change(screen.getByLabelText("Choose file to upload"), {
      target: { files: [new File(["content"], "note.txt")] },
    });
    await waitFor(() =>
      expect(authFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/files/table-1/row-1/attachment?view_id=view-1&canvas_id=canvas-1&element_id=grid-1",
        "upload file",
        expect.objectContaining({
          method: "POST",
          body: expect.any(FormData),
          headers: { "x-workspace-id": "workspace-1" },
        }),
      ),
    );
  });

  it("does not offer upload for an empty file in a read-only grid", () => {
    const authFetch = jest.fn();
    render(
      <FileCell
        value={null}
        rowId="row-1"
        columnField="attachment"
        focusTableId="table-1"
        viewId="view-1"
        allowEdit={false}
        authFetch={authFetch}
        workspaceId="workspace-1"
      />,
    );
    expect(screen.getByText("No file")).toBeInTheDocument();
    expect(screen.queryByTitle("Upload file")).not.toBeInTheDocument();
    expect(screen.queryByLabelText("Choose file to upload")).toBeNull();
    expect(authFetch).not.toHaveBeenCalled();
  });

  it("scopes downloads to the bound view", async () => {
    const authFetch = jest.fn().mockResolvedValue({});
    render(
      <FileCell
        value={{ filename: "note.txt", size_bytes: 7 }}
        rowId="row-1"
        columnField="attachment"
        focusTableId="table-1"
        viewId="view-1"
        allowEdit={false}
        authFetch={authFetch}
        workspaceId="workspace-1"
      />,
    );
    fireEvent.click(screen.getByTitle("Download"));
    expect(screen.queryByTitle("Replace file")).not.toBeInTheDocument();
    expect(screen.queryByTitle("Remove file")).not.toBeInTheDocument();
    await waitFor(() =>
      expect(authFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/files/table-1/row-1/attachment?redirect=false&view_id=view-1",
        "download file",
        { headers: { "x-workspace-id": "workspace-1" } },
      ),
    );
  });
});

describe("FileCell pending upload context", () => {
  it("does not refresh a new selection when an earlier removal completes", async () => {
    let finish!: () => void;
    const authFetch = jest.fn(
      () =>
        new Promise<void>((resolve) => {
          finish = resolve;
        }),
    );
    const onUploaded = jest.fn();
    const onRefresh = jest.fn();
    const props = {
      value: { filename: "note.txt", size_bytes: 7 },
      rowId: "row-1",
      columnField: "attachment",
      focusTableId: "table-1",
      viewId: "view-1",
      workspaceId: "ws-1",
      allowEdit: true,
      authFetch,
      onUploaded,
      onRefresh,
    };
    const { rerender } = render(<FileCell {...props} />);
    fireEvent.click(screen.getByTitle("Remove file"));
    fireEvent.click(screen.getByRole("button", { name: "Remove" }));
    rerender(<FileCell {...props} rowId="row-2" />);
    await act(async () => finish());
    expect(authFetch).toHaveBeenCalledTimes(1);
    expect(onUploaded).not.toHaveBeenCalled();
    expect(onRefresh).not.toHaveBeenCalled();
    expect(screen.getByTitle("Remove file")).toBeEnabled();
  });
  it.each(["workspaceId", "viewId", "focusTableId", "rowId", "columnField"])(
    "suppresses old completion after %s changes",
    async (field) => {
      let finish!: (value: unknown) => void;
      const authFetch = jest.fn(
        () =>
          new Promise((resolve) => {
            finish = resolve;
          }),
      );
      const onUploaded = jest.fn();
      const onRefresh = jest.fn();
      const props = {
        value: null,
        rowId: "row-1",
        columnField: "resume",
        focusTableId: "table-1",
        viewId: "view-1",
        workspaceId: "ws-1",
        allowEdit: true,
        authFetch,
        onUploaded,
        onRefresh,
      };
      const { rerender } = render(<FileCell {...props} />);
      fireEvent.change(screen.getByLabelText("Choose file to upload"), {
        target: { files: [new File(["content"], "resume.txt")] },
      });
      rerender(<FileCell {...props} {...{ [field]: "changed" }} />);
      await act(async () => {
        finish({ filename: "resume.txt" });
      });
      expect(authFetch).toHaveBeenCalledTimes(1);
      expect(onUploaded).not.toHaveBeenCalled();
      expect(onRefresh).not.toHaveBeenCalled();
    },
  );
  it("suppresses completion after unmount", async () => {
    let finish!: (value: unknown) => void;
    const authFetch = jest.fn(
      () =>
        new Promise((resolve) => {
          finish = resolve;
        }),
    );
    const onUploaded = jest.fn();
    const { unmount } = render(
      <FileCell
        value={null}
        rowId="row-1"
        columnField="resume"
        focusTableId="table-1"
        viewId="view-1"
        workspaceId="ws-1"
        allowEdit
        authFetch={authFetch}
        onUploaded={onUploaded}
      />,
    );
    fireEvent.change(screen.getByLabelText("Choose file to upload"), {
      target: { files: [new File(["content"], "resume.txt")] },
    });
    unmount();
    await act(async () => {
      finish({ filename: "resume.txt" });
    });
    expect(onUploaded).not.toHaveBeenCalled();
  });
});
